import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, Clock, FileText } from "lucide-react";
import { BudgetFormData, BudgetPriority, BudgetValidityType } from "@/types/budget";

interface BasicInfoSectionProps {
  clients: Array<{ id: string; name: string; document?: string; email?: string }>;
  users: Array<{ id: string; name: string; email?: string }>;
  className?: string;
}

const priorityOptions: Array<{ value: BudgetPriority; label: string; color: string }> = [
  { value: "low", label: "Baixa", color: "bg-muted text-muted-foreground" },
  { value: "medium", label: "Média", color: "bg-blue-500/10 text-blue-700 dark:text-blue-300" },
  { value: "high", label: "Alta", color: "bg-orange-500/10 text-orange-700 dark:text-orange-300" },
  { value: "urgent", label: "Urgente", color: "bg-red-500/10 text-red-700 dark:text-red-300" },
];

const validityTypeOptions: Array<{ value: BudgetValidityType; label: string; description: string }> = [
  { value: "days", label: "Dias", description: "Válido por X dias a partir da emissão" },
  { value: "date", label: "Data", description: "Válido até uma data específica" },
  { value: "unlimited", label: "Ilimitado", description: "Sem data de validade" },
];

export function BasicInfoSection({ clients, users, className = "" }: BasicInfoSectionProps) {
  const { control, watch, setValue } = useFormContext<BudgetFormData>();

  const watchedValidityType = watch("validity_type");

  return (
    <div className={`space-y-6 ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Informações Principais
          </CardTitle>
          <CardDescription>
            Informações básicas do orçamento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Client Selection */}
          <FormField
            control={control}
            name="client_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cliente *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        <div className="flex flex-col items-start">
                          <span className="font-medium">{client.name}</span>
                          {client.document && (
                            <span className="text-sm text-muted-foreground">
                              {client.document}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Selecione o cliente para este orçamento
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Title and Description */}
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título do Orçamento</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Desenvolvimento de Website"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Título descritivo para identificar o orçamento
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prioridade</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a prioridade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {priorityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className={option.color}>
                              {option.label}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Define a urgência deste orçamento
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Descreva brevemente o escopo deste orçamento..."
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Descrição detalhada dos serviços propostos
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Validade e Responsável
          </CardTitle>
          <CardDescription>
            Configure o período de validade e o responsável pelo orçamento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Validity Type */}
            <FormField
              control={control}
              name="validity_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Validade</FormLabel>
                  <Select onValueChange={(value) => {
                    field.onChange(value);
                    // Clear related fields when type changes
                    setValue("validity_days", undefined);
                    setValue("validity_date", "");
                  }} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de validade" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {validityTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex flex-col items-start">
                            <span className="font-medium">{option.label}</span>
                            <span className="text-sm text-muted-foreground">
                              {option.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Como a validade será definida
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Responsible User */}
            <FormField
              control={control}
              name="responsible_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Responsável
                  </FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o responsável" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex flex-col items-start">
                            <span className="font-medium">{user.name}</span>
                            {user.email && (
                              <span className="text-sm text-muted-foreground">
                                {user.email}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Responsável pelo acompanhamento do orçamento
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Validity Days or Date - Conditional rendering based on type */}
          {watchedValidityType === "days" && (
            <FormField
              control={control}
              name="validity_days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Dias de Validade
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="365"
                      placeholder="7"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                    />
                  </FormControl>
                  <FormDescription>
                    Número de dias que o orçamento permanecerá válido
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {watchedValidityType === "date" && (
            <FormField
              control={control}
              name="validity_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Data de Validade
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Data limite para aceitação do orçamento
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termos e Condições</CardTitle>
          <CardDescription>
            Configure os termos, condições e forma de pagamento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              control={control}
              name="payment_terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condições de Pagamento</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: 30 dias, 50% à vista + 50% em 30 dias"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Como e quando o pagamento será realizado
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="delivery_terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Condições de Entrega</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Imediato, 15 dias úteis"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Prazo e condições para entrega dos serviços
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={control}
            name="terms"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Termos Contratuais</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Termos e condições específicas deste orçamento..."
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Termos personalizados para este orçamento
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}